import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import module from "./VerificationCode.module.css";
import {
  Alert,
  Button,
  CircularProgress,
  Input,
  Snackbar,
  useMediaQuery,
} from "@mui/material";
import PrimaryLinkButton from "../ui/PrimaryLinkButton";
import Password from "../ui/input/Password";
import { Auth } from "aws-amplify";
import { useTheme } from "@emotion/react";

const VerificationCode = ({
  handleAfterConform,
  isPassword = false,
  buttonId,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isPc = useMediaQuery(theme.breakpoints.up("md"));

  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const validation = (values) => {
    // check if all inputs are filled
    const isFilled = values.every((value) => !!value);
    if (!isFilled) {
      alert("全ての入力欄に入力してください。");
      return null;
    }
    // check if all inputs are numbers
    const isNumber = values.every((value) => !isNaN(value));
    if (!isNumber) {
      alert("数字を入力してください。");
      return null;
    }
    // check if all inputs are 1 digit
    const isOneDigit = values.every((value) => value.length === 1);
    if (!isOneDigit) {
      alert("1桁の数字を入力してください。");
      return null;
    }
    // check if all inputs are 6 digits
    const isSixDigit = values.every((value) => value.length === 1);
    if (!isSixDigit) {
      alert("6桁の数字を入力してください。");
      return null;
    }
    const email = localStorage.getItem("signUpEmail");
    if (!email) {
      alert("もう一度登録してください");
      navigate("/signup");
    }
    return null;
  };

  const handleSubmit = async (e) => {
    try {
      e.preventDefault();
      const formNum = 5;
      const values = [];
      for (let i = 0; i <= formNum; i++) {
        const input = document.getElementById(`conform_${i}`);
        values.push(input.value);
      }
      validation(values);
      const email = localStorage.getItem("signUpEmail");
      const code = values.join("");
      if (isPassword) {
        const password = document.getElementById("password").value;
        if (!password) {
          alert("パスワードを入力してください");
          return null;
        }
        await Auth.forgotPasswordSubmit(email, code, password);
      } else {
        await Auth.confirmSignUp(email, code);
      }
      handleAfterConform();
      localStorage.removeItem("signUpEmail");
      navigate("/login");
    } catch (e) {
      console.log(e);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    const currentNum = Number(e.target.name.split("_")[1]);

    const deletePrevInputValue = () => {
      const prevNum = currentNum - 1;
      if (prevNum < 0) {
        return null;
      }
      const prevInput = document.getElementById(`conform_${prevNum}`);
      prevInput.focus();
    };

    const focusToNextInput = () => {
      const nextNum = currentNum + 1;
      const nextInput = document.getElementById(`conform_${nextNum}`);
      if (!!nextInput) {
        nextInput.focus();
      }
    };

    if (isNaN(value) || value.length > 1) {
      e.target.value = e.target.value.slice(0, 1);
      return null;
    }
    if (value.length === 0) {
      deletePrevInputValue();
      return null;
    }
    const isLastInput = currentNum === 5;
    if (isLastInput || isNaN(currentNum)) {
      return null;
    }
    focusToNextInput();
  };
  /** autoFocusが別Componentだと動かなかったため、propsだけ共通化して対応 */
  const formProps = (index) => {
    return {
      id: `conform_${index}`,
      name: `conform_${index}`,
      tabIndex: index,
      sx: {
        width: "45px",
        height: "50px",
        margin: "0 5px",
        border: "1px solid #ccc",
        borderRadius: "5px",
      },
      variant: "outlined",
      size: "small",
      type: isPc ? "number" : "text",
      className: module.input,
      onChange: handleInputChange,
    };
  };

  const handleReSend = async () => {
    try {
      setIsLoading(true);
      const email = localStorage.getItem("signUpEmail");
      await Auth.resendSignUp(email);
      setIsLoading(false);
      setOpen(true);
    } catch (e) {
      setIsLoading(false);
      alert(`もう一度お試しください。${e}}`);
      console.log(e);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <div className={module.container}>
      <div className={module.wrapSub}>
        <p>
          ご登録されているメールアドレス宛に
          <br />
          確認コードを送りました。
          <br />
          メールを確認し、6桁の確認コードを入力してください。
        </p>
      </div>
      <div className={module.wrapConformCode}>
        <form className={module.form} onSubmit={handleSubmit}>
          {isPassword && (
            <div className={`${module.wrapLabel} ${module.password}`}>
              <div>
                <span>新しいパスワードを入力してください</span>
              </div>
              <Password />
            </div>
          )}
          <div className={module.wrapLabel}>
            <span>確認コード</span>
          </div>
          <div className={module.wrapInputs}>
            <Input {...formProps(0)} autoFocus={true} />
            <Input {...formProps(1)} />
            <Input {...formProps(2)} />
            <Input {...formProps(3)} />
            <Input {...formProps(4)} />
            <Input {...formProps(5)} />
          </div>
          <PrimaryLinkButton label={"完了"} type={"submit"} id={buttonId} />

          {isLoading ? (
            <CircularProgress color="success" sx={{ marginTop: "32px" }} />
          ) : (
            <Button
              variant="outlined"
              sx={{
                fontSize: "12px",
                fontWeight: "bold",
                color: "#15B855",
                borderColor: "#15B855",
                padding: "8px 16px",
                marginTop: "32px",
              }}
              onClick={handleReSend}
            >
              もう一度メールを送る
            </Button>
          )}
          {open && (
            <Snackbar
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
              open={open}
              onClose={handleClose}
              key={"top" + "center"}
              security="success"
              autoHideDuration={6000}
            >
              <Alert severity="success">検証コードを再度送信しました</Alert>
            </Snackbar>
          )}
        </form>
      </div>
    </div>
  );
};

export default VerificationCode;
